import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { AutonomyModeSchema, RiskClassSchema } from "./capabilities.js";
import { ArtifactRefSchema } from "./artifacts.js";
import { IsoDateTimeSchema, JsonObjectSchema, OpaqueIdSchema, Sha256Schema, SubjectIdSchema } from "./primitives.js";
export const RunStateSchema = Type.Union([
    Type.Literal("queued"), Type.Literal("awaiting_approval"), Type.Literal("running"), Type.Literal("succeeded"),
    Type.Literal("failed"), Type.Literal("cancel_requested"), Type.Literal("canceled"), Type.Literal("rejected"), Type.Literal("expired"),
], { $id: "carbon.operations.run-state/v1" });
export const RunInputSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Za-z][A-Za-z0-9_-]*$" }),
    ref: ArtifactRefSchema,
    media_type: Type.String({ minLength: 3, maxLength: 128, pattern: "^[A-Za-z0-9.+-]+/[A-Za-z0-9.+-]+$" }), sha256: Sha256Schema,
}, { additionalProperties: false });
export const OnBehalfOfSchema = Type.Object({
    subject_id: SubjectIdSchema, subject_type: Type.Union([Type.Literal("user"), Type.Literal("service")]),
}, { additionalProperties: false });
export const RunCreateSchema = Type.Object({
    schema: Type.Literal("carbon.operations.run.create/v1"), request_id: OpaqueIdSchema, tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    intent: Type.Union([Type.Literal("project_work"), Type.Literal("task_execution"), Type.Literal("information")]),
    capability_id: Type.String({ minLength: 1, maxLength: 160 }), goal: Type.String({ minLength: 1, maxLength: 2_000 }),
    inputs: Type.Array(RunInputSchema, { maxItems: 100 }), requested_autonomy: AutonomyModeSchema, on_behalf_of: OnBehalfOfSchema,
    event_sink_id: Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Za-z0-9][A-Za-z0-9_-]*$" }),
    correlation_id: OpaqueIdSchema, expires_at: IsoDateTimeSchema, metadata: JsonObjectSchema,
}, { additionalProperties: false, $id: "carbon.operations.run.create/v1" });
export const RunLinksSchema = Type.Object({
    self: Type.String({ pattern: "^/api/carbon-operations/v1/runs/" }), events: Type.String({ pattern: "^/api/carbon-operations/v1/runs/" }),
}, { additionalProperties: false });
export const RunResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.run/v1"), run_id: OpaqueIdSchema, request_id: OpaqueIdSchema, tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    state: RunStateSchema, risk_class: RiskClassSchema, approval_required: Type.Boolean(),
    approval_id: Type.Optional(OpaqueIdSchema),
    policy_decision: Type.Optional(Type.Union([Type.Literal("allowed"), Type.Literal("approval_required"), Type.Literal("rejected")])),
    artifact_refs: Type.Optional(Type.Array(ArtifactRefSchema)),
    evidence_refs: Type.Optional(Type.Array(ArtifactRefSchema)),
    failure_code: Type.Optional(Type.String({ minLength: 1, maxLength: 100 })), created_at: IsoDateTimeSchema,
    updated_at: Type.Optional(IsoDateTimeSchema), started_at: Type.Optional(IsoDateTimeSchema), finished_at: Type.Optional(IsoDateTimeSchema),
    links: RunLinksSchema,
}, { additionalProperties: false, $id: "carbon.operations.run/v1" });
export const RunCancelSchema = Type.Object({
    schema: Type.Literal("carbon.operations.run.cancel/v1"), reason: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
}, { additionalProperties: false, $id: "carbon.operations.run.cancel/v1" });
export const ApprovalDecisionSchema = Type.Object({
    schema: Type.Literal("carbon.operations.approval.decision/v1"), decision: Type.Union([Type.Literal("approve"), Type.Literal("deny")]),
    reason: Type.String({ minLength: 1, maxLength: 1_000 }), expected_action_sha256: Sha256Schema, expires_at: IsoDateTimeSchema,
}, { additionalProperties: false, $id: "carbon.operations.approval.decision/v1" });
export const EventTypeSchema = Type.Union([
    Type.Literal("run.queued"), Type.Literal("policy.rejected"), Type.Literal("approval.requested"), Type.Literal("approval.decided"),
    Type.Literal("run.started"), Type.Literal("action.started"), Type.Literal("action.succeeded"), Type.Literal("action.failed"),
    Type.Literal("artifact.created"), Type.Literal("run.succeeded"), Type.Literal("run.failed"), Type.Literal("run.cancel_requested"),
    Type.Literal("run.canceled"), Type.Literal("run.expired"),
]);
export const RunEventSchema = Type.Object({
    schema: Type.Literal("carbon.operations.event/v1"), event_id: OpaqueIdSchema, sequence: Type.Integer({ minimum: 1 }), type: EventTypeSchema,
    occurred_at: IsoDateTimeSchema, tenant_id: OpaqueIdSchema, workspace_id: OpaqueIdSchema, run_id: OpaqueIdSchema,
    correlation_id: OpaqueIdSchema, payload: JsonObjectSchema,
}, { additionalProperties: false, $id: "carbon.operations.event/v1" });
export const RunEventsResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.events/v1"), events: Type.Array(RunEventSchema), next_sequence: Type.Integer({ minimum: 1 }),
}, { additionalProperties: false, $id: "carbon.operations.events/v1" });
// Query strings are not type-coerced: limit is a decimal 1-100; the server default is 25.
const ListLimitSchema = Type.Optional(Type.String({ pattern: "^(100|[1-9][0-9]?)$" }));
// Opaque keyset position; clients pass it back unchanged.
export const ListCursorSchema = Type.String({ minLength: 1, maxLength: 512, pattern: "^[A-Za-z0-9_-]+$" });
export const RunListQuerySchema = Type.Object({
    workspace_id: Type.Optional(OpaqueIdSchema), state: Type.Optional(RunStateSchema),
    correlation_id: Type.Optional(OpaqueIdSchema), limit: ListLimitSchema, cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false });
export const RunListResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.runs/v1"), runs: Type.Array(RunResponseSchema),
    next_cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false, $id: "carbon.operations.runs/v1" });
export const ApprovalStateSchema = Type.Union([
    Type.Literal("pending"), Type.Literal("approved"), Type.Literal("rejected"), Type.Literal("canceled"), Type.Literal("expired"),
]);
export const ApprovalListQuerySchema = Type.Object({
    workspace_id: Type.Optional(OpaqueIdSchema), state: Type.Optional(ApprovalStateSchema), limit: ListLimitSchema,
    cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false });
export const ApprovalSummarySchema = Type.Object({
    approval_id: OpaqueIdSchema, run_id: OpaqueIdSchema, workspace_id: OpaqueIdSchema, state: ApprovalStateSchema,
    capability: Type.String({ minLength: 1, maxLength: 160 }), requested_by: SubjectIdSchema, expires_at: IsoDateTimeSchema,
    expected_action_sha256: Sha256Schema,
}, { additionalProperties: false });
export const ApprovalListResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.approvals/v1"), approvals: Type.Array(ApprovalSummarySchema),
    next_cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false, $id: "carbon.operations.approvals/v1" });
/** The complete RunCreate contract check: every required field, nothing additional. For payloads read back from storage. */
export function isRunCreate(value) {
    return Value.Check(RunCreateSchema, value);
}
//# sourceMappingURL=runs.js.map